import React, { useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

// Setup mode card for update_store_settings. The name and coordinates are
// editable here: Lumi proposes city-level coordinates, and the admin can
// correct them before confirming. The backend re-validates every value.
export default function LocationSettingsProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
  confirmUrl = API_ops_assistant_confirm_action,
  scopeParams,
}) {
  const summary = proposal.summary || {};
  const [status, setStatus] = useState(proposal.status || "pending");
  const [loadingAction, setLoadingAction] = useState(null);
  const [newName, setNewName] = useState(summary.new_name || "");
  const [latitude, setLatitude] = useState(summary.latitude == null ? "" : String(summary.latitude));
  const [longitude, setLongitude] = useState(summary.longitude == null ? "" : String(summary.longitude));
  const [result, setResult] = useState(null);

  React.useEffect(() => {
    if ((proposal.status === "cancelled" || proposal.status === "rejected") && status === "pending") {
      setStatus("cancelled");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal.status]);

  const storeName = summary.store_name || summary.type || "this location";
  const lat = latitude.trim() === "" ? null : Number(latitude);
  const lon = longitude.trim() === "" ? null : Number(longitude);
  const coordsEntered = lat !== null || lon !== null;
  const coordsValid =
    !coordsEntered ||
    (lat !== null && lon !== null && Number.isFinite(lat) && Number.isFinite(lon) &&
      lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180);
  const coordsEdited =
    String(summary.latitude ?? "") !== latitude.trim() || String(summary.longitude ?? "") !== longitude.trim();
  const nothingToChange =
    proposal.tool_name !== "set_location_address" && !coordsEntered && newName.trim() === "";
  const confirmDisabled = loadingAction !== null || !coordsValid || nothingToChange;

  const handleAction = async (action) => {
    setLoadingAction(action);
    try {
      const payload = {
        ...(scopeParams || { store_id: homeStore }),
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action,
      };
      if (action === "confirm") {
        payload.overrides = {
          new_name: newName.trim() || null,
          latitude: lat,
          longitude: lon,
        };
      }
      const res = await postRightworkAPI(confirmUrl, payload);
      if (res.status === 200) {
        const payloadResult = res?.data?.redux?.payload?.result;
        setResult(payloadResult && typeof payloadResult === "object" ? payloadResult : null);
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to process action.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to process action.");
    }
    setLoadingAction(null);
  };

  if (status === "confirmed") {
    const parts = [];
    if (result?.store_name) parts.push(result.store_name);
    if (result?.latitude != null && result?.longitude != null) {
      parts.push(`${Number(result.latitude).toFixed(4)}, ${Number(result.longitude).toFixed(4)}`);
    }
    if (result?.timezone) parts.push(`timezone ${result.timezone}`);
    if (result?.jurisdiction) parts.push(`labor laws: ${result.jurisdiction}`);
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-sm my-2 border bg-green-50 border-green-200 text-green-700">
        <CheckCircleIcon fontSize="small" className="mt-0.5 shrink-0 text-green-600" />
        <div className="min-w-0">
          <div className="font-medium">{summary.title || "Location updated"}</div>
          {parts.length > 0 && <div className="text-xs mt-1">{parts.join(" · ")}</div>}
        </div>
      </div>
    );
  }

  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>Action cancelled</span>
      </div>
    );
  }

  const inputClass =
    "w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400 bg-white disabled:bg-gray-50";
  const fieldId = (key) => `location-settings-${proposal.proposal_id}-${key}`;

  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <HourglassEmptyIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">{summary.title || "Update location settings"}</span>
      </div>
      <div className="px-3 py-3 space-y-3">
        <div className="text-sm text-gray-700">
          <span className="text-gray-500">Location: </span>
          <span className="font-medium">{storeName}</span>
        </div>
        {summary.address && (
          <div className="text-sm text-gray-700">
            <span className="text-gray-500">Address: </span>
            <span className="font-medium">{summary.address}</span>
          </div>
        )}
        {summary.jurisdiction_label && (
          <div className="text-sm text-gray-700">
            <span className="text-gray-500">Labor law jurisdiction: </span>
            <span className="font-medium">{summary.jurisdiction_label}</span>
            <span className="text-xs text-gray-500"> (from the address)</span>
          </div>
        )}
        {proposal.tool_name !== "set_location_address" && (
        <div>
          <label htmlFor={fieldId("name")} className="block text-xs font-medium text-gray-600 mb-0.5">
            New name (optional)
          </label>
          <input
            id={fieldId("name")}
            data-testid="location-settings-name"
            type="text"
            value={newName}
            placeholder={storeName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={loadingAction !== null}
            className={inputClass}
          />
        </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor={fieldId("lat")} className="block text-xs font-medium text-gray-600 mb-0.5">
              Latitude
            </label>
            <input
              id={fieldId("lat")}
              data-testid="location-settings-latitude"
              type="text"
              inputMode="decimal"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              disabled={loadingAction !== null}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor={fieldId("lon")} className="block text-xs font-medium text-gray-600 mb-0.5">
              Longitude
            </label>
            <input
              id={fieldId("lon")}
              data-testid="location-settings-longitude"
              type="text"
              inputMode="decimal"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              disabled={loadingAction !== null}
              className={inputClass}
            />
          </div>
        </div>
        {!coordsValid && (
          <div role="alert" className="text-xs text-red-600">
            Enter both a latitude (-90 to 90) and a longitude (-180 to 180), or leave both blank.
          </div>
        )}
        <div className="text-xs text-gray-600">
          {summary.timezone && !coordsEdited
            ? `Timezone for these coordinates: ${summary.timezone}.`
            : coordsEntered
            ? "The timezone is recalculated from the coordinates when you confirm."
            : "Coordinates set the location's timezone, weather, and local events."}
          {" "}
          {proposal.summary?.pin_source === "street_address"
            ? "The pin was geocoded from the street address; clock-ins are geofenced to it, so edit it here if it looks off."
            : proposal.summary?.pin_source === "city_level"
            ? "The pin is a city-level estimate and clock-in geofencing uses it: correct it here, or verify it on the Locations page after confirming."
            : "City-level coordinates are fine; edit them here if they are off."}
        </div>
        {proposal.summary?.jurisdiction_fallback && (
          <div className="text-xs text-amber-700">
            City-level labor law checks are not available for this location; the broader jurisdiction's settings apply.
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          type="button"
          onClick={() => handleAction("reject")}
          disabled={loadingAction !== null}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => handleAction("confirm")}
          disabled={confirmDisabled}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loadingAction === "confirm" && <CircularProgress size={14} sx={{ color: "white" }} />}
          Confirm
        </button>
      </div>
    </div>
  );
}
